import React, { type ComponentType, type CSSProperties } from 'react';
import { type SiteStyles, DEFAULT_SITE_STYLES } from '@wix/payments-common';
import { getSiteCssVariables, getComponentCssVariables } from '@wix/tpa-style-webpack-plugin/css-vars';
import { isRTLLocale } from '@wix/cashier-common/dist/src/utils/isRTLLocale';
import { TPAComponentsProvider } from '@wix/wix-ui-tpa/cssVars';
import { type DeviceType } from '../types/DeviceType';
import { captureMessage } from './errorReporter';

type WithStylesProps = {
  locale: string;
  deviceType?: DeviceType;
};

const prefixSelector = ``;

function parseCssVariables(cssString: string): CSSProperties {
  const cssObject = {};
  const lines = cssString.split(';');

  lines.forEach((line: string) => {
    const [key, value] = line.split(':').map((part) => part.trim());
    if (key && value) {
      cssObject[key] = value;
    }
  });

  return cssObject;
}

export const withStyles = <P extends WithStylesProps>(App: ComponentType<P>) => {
  return (props: P) => {
    const isMobile = props.deviceType === 'mobile';

    const getCssVariables = (siteStyles: SiteStyles, cssOptions: Record<string, string | boolean>): CSSProperties => {
      const siteCssVars = getSiteCssVariables(siteStyles.siteColors, siteStyles.siteTextPresets);
      const widgetCssVars = getComponentCssVariables(
        siteStyles,
        {},
        {},
        {
          prefixSelector,
          isRTL: cssOptions.isRTL as boolean,
          isMobile,
          usesCssPerBreakpoint: true,
          isEditorX: false,
        },
        {},
      );

      return parseCssVariables(`
        ${siteCssVars.stylesheet}
        ${widgetCssVars.stylesheet}
      `);
    };

    try {
      const siteStyles: SiteStyles = DEFAULT_SITE_STYLES;

      const cssOptions = {
        prefixSelector,
        isRTL: isRTLLocale(props.locale),
        strictMode: false,
      };
      const rootStyle = document.documentElement.style;

      Object.entries(getCssVariables(siteStyles, cssOptions)).forEach(([key, value]) => {
        rootStyle.setProperty(key, value);
      });
    } catch (e) {
      captureMessage(e);
      return <App {...props} />;
    }

    return (
      <TPAComponentsProvider
        value={{
          mobile: isMobile,
          rtl: isRTLLocale(props.locale),
          cssVars: true,
        }}
      >
        <App {...props} />
      </TPAComponentsProvider>
    );
  };
};
